import {
  CredentialType,
  CredentialTypeSelection,
} from '@/sections/asset/components/tabs/credentials/credentialForms';
import type { SeedStepProps } from '@/sections/seeds/components/modals/AddSeedByTypeModal/AddSeedByTypeModal.types';

import { useConfigureCredential } from './useConfigureCredential';

/**
 * CredentialApproachStep — the wizard's credential APPROACH-selection step. Shown
 * only for web seeds when the user chose "Configure" on the preceding
 * CredentialStep (gated via shouldSkip in wizardConfig). Renders ONLY the
 * credential type picker cards (no form body — that lives on the following
 * CredentialFormStep). Fully controlled into `formData.credential.data` via the
 * shared {@link useConfigureCredential} hook (no local secret state — security
 * P2-2). Always validates to true.
 */
export function CredentialApproachStep({
  formData,
  setFormData,
}: SeedStepProps) {
  const state = useConfigureCredential(formData, setFormData);
  // Defensive: shouldSkip already gates this to configure mode, but bail if a
  // stray render reaches it on the skip path.
  if (state.data === null) return null;

  const { data, isWebService, isSingleSeed, updateData } = state;

  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-6">
      <div className="flex flex-col gap-1.5 text-center">
        <h3 className="text-chariot-text-primary text-base font-semibold">
          How should Guard authenticate?
        </h3>
        <p className="text-chariot-text-secondary text-sm">
          Choose the method Guard uses to log in before scanning.
        </p>
      </div>
      <div className="w-full max-w-md">
        <TypeSelection
          isWebService={isWebService}
          isSingleSeed={isSingleSeed}
          selectedType={data.selectedType}
          onTypeChange={type => updateData({ selectedType: type })}
        />
      </div>
    </div>
  );
}

interface TypeSelectionProps {
  isWebService: boolean;
  isSingleSeed: boolean;
  selectedType: CredentialType;
  onTypeChange: (type: CredentialType) => void;
}

const DISABLED_NOTE =
  'Only available when adding a single seed (interactive login flows run per-application).';

/**
 * Renders the credential type picker via the shared CredentialTypeSelection
 * (single source of truth for the option arrays). For a single seed every
 * option is selectable. For multiple seeds the interactive COMPLEX_LOGIN +
 * RECIPE options are disabled with an inline note (D4) — they must not fan out
 * to N agentic/recipe flows.
 */
function TypeSelection({
  isWebService,
  isSingleSeed,
  selectedType,
  onTypeChange,
}: TypeSelectionProps) {
  return (
    <CredentialTypeSelection
      isWebService={isWebService}
      selectedType={selectedType}
      onTypeChange={onTypeChange}
      disabledTypes={
        isSingleSeed
          ? undefined
          : {
              [CredentialType.COMPLEX_LOGIN]: DISABLED_NOTE,
              [CredentialType.RECIPE]: DISABLED_NOTE,
            }
      }
    />
  );
}
